import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

function formatFieldValue(value, format) {
  if (value == null || value === '') return '—';
  if (format === 'date') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return '—';
  return String(value);
}

/**
 * Reusable read-only details popup for Reports tables.
 * Props:
 *  - open, onClose, title
 *  - fields: [{ label, value, format? }]  format: 'date' | undefined
 */
export default function ReportDetailsModal({ open, onClose, title = 'Details', fields = [] }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const content = (
    <div
      className="fixed inset-0 flex items-center justify-center p-3 md:p-4 font-nunito"
      style={{ zIndex: 99999 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-details-title"
    >
      <div
        className="absolute inset-0 bg-black/20"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col bg-white rounded-[18px] border border-[#EEF1F5] shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between shrink-0 px-6 pt-6 pb-2">
          <h2 id="report-details-title" className="text-lg font-semibold text-[#101828]">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-full border border-[#D0D5DD] text-[#667085] flex items-center justify-center bg-white hover:bg-[#F6F7FA] transition"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <hr className="border-0 border-t border-[#EEF1F5] mt-2" />
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {fields.length === 0 ? (
            <div className="py-8 text-center text-gray-500 text-sm">No details available</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {fields.map((field) => (
                <div key={field.key || field.label} className="min-w-0">
                  <div className="text-gray-500 mb-0.5">{field.label}</div>
                  <div className="text-[#101828] font-medium break-words">
                    {formatFieldValue(field.value, field.format)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="shrink-0 px-6 py-4 border-t border-[#EEF1F5] flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-[#D0D5DD] bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
